// Governance Vuex module
import { createBaseModule } from './base'
import { governanceService } from '@/services/governanceService'

const customState = {
  currentProposal: null,
  filters: {
    type: null,
    status: null,
    workstream_id: null,
    mine: false
  },
  tabs: 'active' // active or completed
}

const customGetters = {
  activeProposals: state => 
    state.items.filter(p => p.status === 'active'),
  
  completedProposals: state => 
    state.items.filter(p => p.status === 'completed'),
  
  filteredProposals: state => {
    let filtered = state.items
    
    if (state.filters.type) {
      filtered = filtered.filter(p => p.proposal_type === state.filters.type)
    }
    if (state.filters.status) {
      filtered = filtered.filter(p => p.status === state.filters.status)
    }
    if (state.filters.workstream_id) {
      filtered = filtered.filter(p => p.workstream_id === state.filters.workstream_id)
    }
    if (state.filters.mine) {
      filtered = filtered.filter(p => p.created_by === state.currentUser?.id)
    }
    
    return filtered
  },
  
  currentProposal: state => state.currentProposal,
  
  currentTab: state => state.tabs,
  
  getProposalVoteTally: state => proposalId => {
    const proposal = state.items.find(p => p.id === proposalId)
    return proposal?.vote_tally || { for: 0, against: 0, abstain: 0 }
  },
  
  getUserVote: state => proposalId => {
    const proposal = state.items.find(p => p.id === proposalId)
    return proposal?.user_vote || null
  }
}

const customMutations = {
  SET_CURRENT_PROPOSAL(state, proposal) {
    state.currentProposal = proposal
  },
  
  CLEAR_CURRENT_PROPOSAL(state) {
    state.currentProposal = null
  },
  
  SET_FILTER(state, { key, value }) {
    if (key in state.filters) {
      state.filters[key] = value
    }
  },
  
  CLEAR_FILTERS(state) {
    state.filters = {
      type: null,
      status: null,
      workstream_id: null,
      mine: false
    }
  },
  
  SET_TAB(state, tab) {
    state.tabs = tab
  },
  
  UPDATE_PROPOSAL_VOTE_TALLY(state, { proposalId, voteTally }) {
    const index = state.items.findIndex(p => p.id === proposalId)
    if (index >= 0) {
      state.items[index] = {
        ...state.items[index],
        vote_tally: voteTally
      }
    }
    if (state.currentProposal?.id === proposalId) {
      state.currentProposal.vote_tally = voteTally
    }
  },
  
  SET_USER_VOTE(state, { proposalId, vote }) {
    const index = state.items.findIndex(p => p.id === proposalId)
    if (index >= 0) {
      state.items[index] = {
        ...state.items[index],
        user_vote: vote
      }
    }
    if (state.currentProposal?.id === proposalId) {
      state.currentProposal.user_vote = vote
    }
  }
}

const customActions = {
  async fetchProposals({ commit }, params = {}) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    try {
      const data = await governanceService.listProposals(params)
      const proposals = Array.isArray(data) ? data : (data?.proposals || [])
      commit('SET_ITEMS', proposals)
      return proposals
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to load proposals')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchProposal({ commit, state }, proposalId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    try {
      const data = await governanceService.getProposal(proposalId)
      const existing = state.items.find(p => p.id === proposalId) || {}
      // Deep-merge key sub-objects to preserve fields present only in list payload
      const merged = {
        ...existing,
        ...data,
        metadata: { ...(existing?.metadata || {}), ...(data?.metadata || {}) },
        proposal_data: { ...(existing?.proposal_data || {}), ...(data?.proposal_data || {}) }
      }
      commit('SET_CURRENT_PROPOSAL', merged)
      commit('UPDATE_ITEM', { id: proposalId, updates: merged })
      return merged
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to load proposal')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createProposal({ commit, dispatch }, proposalData) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    try {
      const data = await governanceService.createProposal(proposalData)
      commit('ADD_ITEM', data)
      dispatch('showSuccess', 'Proposal created successfully', { root: true })
      return data
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create proposal')
      dispatch('handleError', error, { root: true })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async voteOnProposal({ commit, dispatch }, { proposalId, voteOption, reason = '' }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    try {
      const response = await governanceService.voteOnProposal(proposalId, {
        vote_option: voteOption,
        reason
      })
      
      // Update vote tally
      if (response?.vote_tally) {
        commit('UPDATE_PROPOSAL_VOTE_TALLY', {
          proposalId,
          voteTally: response.vote_tally
        })
      }
      
      // Track user's vote
      const vote = {
        vote_option: voteOption,
        reason,
        voted_at: new Date().toISOString()
      }
      commit('SET_USER_VOTE', { proposalId, vote })
      
      dispatch('showSuccess', 'Vote recorded successfully', { root: true })
      return response
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to cast vote')
      dispatch('handleError', error, { root: true })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteProposal({ commit, dispatch }, proposalId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    try {
      await governanceService.deleteProposal(proposalId)
      commit('REMOVE_ITEM', proposalId)
      dispatch('showSuccess', 'Proposal deleted successfully', { root: true })
      return true
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete proposal')
      dispatch('handleError', error, { root: true })
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  setFilter({ commit }, { key, value }) {
    commit('SET_FILTER', { key, value })
  },

  clearFilters({ commit }) {
    commit('CLEAR_FILTERS')
  },

  setCurrentProposal({ commit }, proposal) {
    commit('SET_CURRENT_PROPOSAL', proposal)
  },

  clearCurrentProposal({ commit }) {
    commit('CLEAR_CURRENT_PROPOSAL')
  },

  setTab({ commit }, tab) {
    commit('SET_TAB', tab)
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR')
  }
}

export default createBaseModule('proposal', governanceService, {
  customState,
  customGetters,
  customMutations,
  customActions,
  idField: 'id'
})
